import { useEffect, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import slide1 from "@/assets/slideshow/slide-1.png";
import slide2 from "@/assets/slideshow/slide-2.png";
import slide3Asset from "@/assets/slideshow/natverkstraff-inbjudan.png.asset.json";

/**
 * BILDSPEL — fyra bilder.
 * Byt enkelt bild genom att ladda upp en ny och ersätta `image`-värdet nedan,
 * eller justera `title` / `text`. Ordningen i listan styr ordningen i bildspelet.
 */
const slides = [
  {
    image: slide1,
    title: "Digitala nätverksträffar",
    text: "Möt företagare från hela Sverige i samma rum, var du än är.",
  },
  {
    image: slide2,
    title: "Samtal i mindre grupper",
    text: "Personliga samtal istället för stora anonyma rum. 3-5 personer per grupp.",
  },
  {
    image: slide3Asset.url,
    title: "Nya kontakter och relationer",
    text: "Bygg förtroende som leder till långsiktiga affärsmöjligheter.",
  },
];

const NetworkSlideshow = () => {
  const [index, setIndex] = useState(0);

  const go = useCallback(
    (dir: number) => setIndex((i) => (i + dir + slides.length) % slides.length),
    []
  );

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % slides.length), 6000);
    return () => clearInterval(id);
  }, []);

  return (
    <section className="py-20 px-4 bg-background">
      <div className="container mx-auto max-w-5xl">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-accent/10 text-accent px-4 py-2 rounded-full mb-4 text-sm font-medium">
            Så känns en träff
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
            Mänskliga möten, digitalt format
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Lugna samtal i mindre grupper, digitalt över Zoom.
          </p>
        </div>

        <div className="relative overflow-hidden rounded-2xl shadow-xl">
          <div
            className="flex transition-transform duration-700 ease-out"
            style={{ transform: `translateX(-${index * 100}%)` }}
          >
            {slides.map((slide) => (
              <figure key={slide.title} className="relative min-w-full">
                <div className="aspect-[16/9] w-full bg-secondary/40">
                  <img
                    src={slide.image}
                    alt={slide.title}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                </div>
                <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-primary/85 via-primary/40 to-transparent p-6 md:p-8">
                  <h3 className="font-serif text-xl md:text-2xl text-primary-foreground">
                    {slide.title}
                  </h3>
                  <p className="text-sm md:text-base text-primary-foreground/85 mt-1 max-w-lg">
                    {slide.text}
                  </p>
                </figcaption>
              </figure>
            ))}
          </div>

          {/* Controls */}
          <button
            type="button"
            onClick={() => go(-1)}
            aria-label="Föregående bild"
            className="absolute left-3 top-1/2 -translate-y-1/2 inline-flex h-10 w-10 items-center justify-center rounded-full bg-card/80 text-primary backdrop-blur-sm transition-colors hover:bg-card"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => go(1)}
            aria-label="Nästa bild"
            className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex h-10 w-10 items-center justify-center rounded-full bg-card/80 text-primary backdrop-blur-sm transition-colors hover:bg-card"
          >
            <ChevronRight className="h-5 w-5" />
          </button>

          {/* Dots */}
          <div className="absolute bottom-3 right-4 flex gap-2">
            {slides.map((slide, i) => (
              <button
                key={slide.title}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`Gå till bild ${i + 1}`}
                className={`h-2 rounded-full transition-all ${
                  i === index ? "w-6 bg-primary-foreground" : "w-2 bg-primary-foreground/50"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default NetworkSlideshow;
